import React, { useState, useEffect } from 'react';
import { Scissors, Trash2, ArrowRight, HelpCircle, Info } from 'lucide-react';

export default function HedgeCalculator() {
  const [length, setLength] = useState(5);
  const [height, setHeight] = useState('low'); // 'low', 'medium', 'high'
  const [sides, setSides] = useState(1); // 1 = 1 side, 2 = 1 side + top, 3 = 2 sider + top
  const [disposal, setDisposal] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);

  // Price configuration
  const prices = {
    low: 28,     // 0-150 cm: 28 kr. pr. meter
    medium: 42,  // 150-250 cm: 42 kr. pr. meter
    high: 65     // over 250 cm: 65 kr. pr. meter
  };

  const disposalPrices = {
    low: 12,
    medium: 18,
    high: 25
  };

  const startupFee = 495;

  useEffect(() => {
    const basePrice = prices[height];
    const extraPrice = disposal ? disposalPrices[height] : 0;
    setTotalPrice((length * (basePrice + extraPrice) * sides) + startupFee);
  }, [length, height, sides, disposal]);

  const handleLengthChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val)) {
      setLength(Math.max(1, Math.min(500, val)));
    }
  };

  const handleApply = () => {
    const heightText = 
      height === 'low' ? 'Lav hæk (0–150 cm)' : 
      height === 'medium' ? 'Mellem hæk (150–250 cm)' : 
      'Høj hæk (over 250 cm)';
      
    const sidesText = 
      sides === 1 ? '1 side' :
      sides === 2 ? '1 side + top' :
      '2 sider + top';
    
    const msg = `Hej Lasse, jeg vil gerne have et uforpligtende tilbud på hækklipning. Her er mine oplysninger fra prisberegneren:
- Hækkens længde: ${length} meter
- Hækkens højde: ${heightText}
- Omfang: ${sidesText}
- Opsamling: ${disposal ? 'Ja' : 'Nej tak'}
- Estimeret pris: ${totalPrice.toLocaleString('da-DK')} kr. (inkl. moms og kørselsgebyr)`;

    // Update URL search params for fallback
    const url = new URL(window.location.href);
    url.searchParams.set('message', msg);
    window.history.pushState({}, '', url);

    // Trigger custom event to prefill contact form
    const event = new CustomEvent('prefill-contact', { detail: msg });
    window.dispatchEvent(event);

    // Smooth scroll to contact section
    const element = document.getElementById('kontakt');
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="w-full max-w-md bg-stone-900/95 backdrop-blur-md border border-stone-800 rounded-2xl p-5 sm:p-6 shadow-2xl text-stone-100 relative overflow-hidden">
      {/* Subtle background glow */}
      <div className="absolute -right-10 -top-10 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />
      
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-emerald-950/80 border border-emerald-800/50 rounded-lg text-emerald-400">
            <Scissors className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white uppercase tracking-wider">Prisberegner</h3>
            <p className="text-xs text-stone-400">Få et lynhurtigt estimat på din hækklipning</p>
          </div>
        </div>

        <div className="space-y-5">
          {/* Length Input */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-semibold text-stone-300">Hækkens længde</label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={length}
                  onChange={handleLengthChange}
                  min="1"
                  max="500"
                  className="w-16 bg-stone-950 border border-stone-800 focus:border-emerald-600 rounded-md px-2 py-1 text-right text-sm font-bold text-emerald-400 outline-none"
                />
                <span className="text-xs text-stone-400 font-medium">meter</span>
              </div>
            </div>
            
            <input
              type="range"
              min="5"
              max="200"
              value={length}
              onChange={(e) => setLength(parseInt(e.target.value, 10))}
              className="w-full h-1.5 bg-stone-950 rounded-lg appearance-none cursor-pointer accent-emerald-500 focus:outline-none"
            />
            <div className="flex justify-between text-[10px] text-stone-500 mt-1">
              <span>5 m</span>
              <span>100 m</span>
              <span>200 m+</span>
            </div>
          </div>

          {/* Height Selection */}
          <div>
            <label className="block text-sm font-semibold text-stone-300 mb-2">Hækkens højde</label>
            <div className="grid grid-cols-3 gap-2">
              {/* Low */}
              <button
                type="button"
                onClick={() => setHeight('low')}
                className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-center transition-all cursor-pointer ${
                  height === 'low'
                    ? 'bg-emerald-950/40 border-emerald-600 text-white shadow-lg shadow-emerald-950/20'
                    : 'bg-stone-950/50 border-stone-800 text-stone-400 hover:border-stone-700 hover:text-stone-300'
                }`}
              >
                <span className="text-xs font-bold block">Lav</span>
                <span className="text-[10px] text-stone-400 block mt-0.5">0–150 cm</span>
              </button>

              {/* Medium */}
              <button
                type="button"
                onClick={() => setHeight('medium')}
                className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-center transition-all cursor-pointer ${
                  height === 'medium'
                    ? 'bg-emerald-950/40 border-emerald-600 text-white shadow-lg shadow-emerald-950/20'
                    : 'bg-stone-950/50 border-stone-800 text-stone-400 hover:border-stone-700 hover:text-stone-300'
                }`}
              >
                <span className="text-xs font-bold block">Mellem</span>
                <span className="text-[10px] text-stone-400 block mt-0.5">150–250 cm</span>
              </button>

              {/* High */}
              <button
                type="button"
                onClick={() => setHeight('high')}
                className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-center transition-all cursor-pointer ${
                  height === 'high'
                    ? 'bg-emerald-950/40 border-emerald-600 text-white shadow-lg shadow-emerald-950/20'
                    : 'bg-stone-950/50 border-stone-800 text-stone-400 hover:border-stone-700 hover:text-stone-300'
                }`}
              >
                <span className="text-xs font-bold block">Høj</span>
                <span className="text-[10px] text-stone-400 block mt-0.5">Over 250 cm</span>
              </button>
            </div>
          </div>

          {/* Sides Selection */}
          <div>
            <label className="block text-sm font-semibold text-stone-300 mb-2">Omfang</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setSides(1)}
                className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-center transition-all cursor-pointer ${
                  sides === 1
                    ? 'bg-emerald-950/40 border-emerald-600 text-white shadow-lg shadow-emerald-950/20'
                    : 'bg-stone-950/50 border-stone-800 text-stone-400 hover:border-stone-700 hover:text-stone-300'
                }`}
              >
                <span className="text-xs font-bold block">1 side</span>
              </button>
              <button
                type="button"
                onClick={() => setSides(2)}
                className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-center transition-all cursor-pointer ${
                  sides === 2
                    ? 'bg-emerald-950/40 border-emerald-600 text-white shadow-lg shadow-emerald-950/20'
                    : 'bg-stone-950/50 border-stone-800 text-stone-400 hover:border-stone-700 hover:text-stone-300'
                }`}
              >
                <span className="text-xs font-bold block">1 side + top</span>
              </button>
              <button
                type="button"
                onClick={() => setSides(3)}
                className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-center transition-all cursor-pointer ${
                  sides === 3
                    ? 'bg-emerald-950/40 border-emerald-600 text-white shadow-lg shadow-emerald-950/20'
                    : 'bg-stone-950/50 border-stone-800 text-stone-400 hover:border-stone-700 hover:text-stone-300'
                }`}
              >
                <span className="text-xs font-bold block">2 sider + top</span>
              </button>
            </div>
          </div>

          {/* Disposal Toggle */}
          <div className="flex items-center justify-between p-3 bg-stone-950/50 border border-stone-800 rounded-xl">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-stone-900 border border-stone-800 rounded-lg text-stone-400">
                <Trash2 className="h-4 w-4" />
              </div>
              <div>
                <span className="text-xs font-semibold text-stone-200 block">Opsamling af haveaffald</span>
                <span className="text-[10px] text-stone-400 block">Jeg rydder op og kører det væk (+{disposalPrices[height]} kr./m pr. side)</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setDisposal(!disposal)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer outline-none ${
                disposal ? 'bg-emerald-600' : 'bg-stone-800'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  disposal ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Price Summary Box */}
          <div className="bg-stone-950 border border-stone-800 rounded-xl p-4">
            <div className="flex justify-between text-xs text-stone-400 mb-1.5">
              <span>Hækklipning ({length}m × {prices[height]}kr × {sides} {sides === 1 ? 'side' : 'sider'})</span>
              <span>{(length * prices[height] * sides).toLocaleString('da-DK')} kr.</span>
            </div>
            {disposal && (
              <div className="flex justify-between text-xs text-stone-400 mb-1.5">
                <span>Opsamling ({length}m × {disposalPrices[height]}kr × {sides} {sides === 1 ? 'side' : 'sider'})</span>
                <span>{(length * disposalPrices[height] * sides).toLocaleString('da-DK')} kr.</span>
              </div>
            )}
            <div className="flex justify-between text-xs text-stone-400 pb-2 border-b border-stone-800/60 mb-2">
              <span>Opstarts- og kørselsgebyr</span>
              <span>{startupFee} kr.</span>
            </div>
            <div className="flex justify-between items-baseline mt-2">
              <span className="text-xs font-bold text-stone-300 uppercase tracking-wider">Estimeret pris</span>
              <div className="text-right">
                <span className="text-2xl font-black text-emerald-400">{totalPrice.toLocaleString('da-DK')} kr.</span>
                <span className="text-[10px] text-stone-500 block">Inkl. moms & materialer</span>
              </div>
            </div>
          </div>

          {/* Servicefradrag */}
          <div className="bg-emerald-950/30 border border-emerald-900/50 rounded-xl p-3 flex items-start gap-3">
            <Info className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-100/80 leading-relaxed">
              <strong>Husk servicefradraget!</strong> Denne ydelse er dækket af servicefradraget, hvilket betyder at du kan trække 26% af arbejdslønnen fra i skat.
            </p>
          </div>

          {/* CTA Button */}
          <button
            type="button"
            onClick={handleApply}
            className="w-full flex items-center justify-center gap-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-500 py-3 rounded-xl transition-all shadow-lg shadow-emerald-950/50 hover:shadow-emerald-950/80 hover:-translate-y-0.5 cursor-pointer"
          >
            Få tilbud med disse mål
            <ArrowRight className="h-4 w-4" />
          </button>

          <p className="text-[10px] text-stone-500 text-center mt-3">
            * OBS: Tilbudsberegneren gælder kun for klipning, ikke beskæring. Kontakt os for at få et tilbud på beskæring.
          </p>
        </div>
      </div>
    </div>
  );
}
